import { Box, Center, Flex, FormControl, FormLabel, StyleProps, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { $email, $isstring, $length, $ok, $UUID } from "foundation-ts/commons";
import { $country } from 'foundation-ts/tsdefaults';
import { TSDate } from "foundation-ts/tsdate";
import { Countries, Nullable, UUID } from "foundation-ts/types";
import { CreateUserBody } from "g1-commons/lib/doxecureClientTypes";
import { vatRegistrationNumber } from "g1-commons/lib/doxecureFunctions";
import { G1Country } from "g1-commons/lib/doxecureLocales";
import { UserFlags } from "g1-commons/lib/doxecureTypes";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { CorporationForm, CorporationFormValues } from "@components/CorporationForm/CorporationForm";
import { ErrorField } from "@components/ErrorField" ;
import { G1Button } from "@components/G1Button";
import { G1Input } from "@components/G1Input";
import { SeaShellBox } from "@components/SeaShellBox" ;
import { useDevice } from "@hooks/useDevice";
import { $g1Color } from "@/theme/G1Style";
import { userRoleAndStatus } from "@utils/functions";
import { G1Icon } from "@utils/icons";
import { ActionButton } from "@utils/TypesAndConstants";

export interface UserParameters extends CreateUserBody {
    apid?: UUID,
    createdAt?: TSDate,
    userFlags?: UserFlags,
    corporationId?: UUID,
    name?: string
} ;

export interface UserInfosProperties extends StyleProps {
    data: Nullable<UserParameters> ;
    actionButtons: ActionButton<UserParameters>[] ;
    countries?: G1Country[] ;
    isAdminCorporate?: boolean ;
    isCreation?: boolean ;
    onBack: () => void ;
} ;

type UserInfosValues = {
    firstName: string,
    lastName: string,
    email: string,
    apid: string
} & CorporationFormValues ;

export const UserInfos = (props: UserInfosProperties) => {
    const { data, actionButtons, maxWidth, visibility, countries, isCreation, onBack } = props;
    const { isMOBILE } = useDevice();
    const [disabledUserInfos, setDisabledUserInfos] = useState($ok(data));
    const [disabledOrganizationName, setDisabledOrganizationName] = useState(false);
    const [disabledVATNumber, setDisabledVATNumber] = useState(false);
    const [disabledAddress, setDisabledAddress] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(0);
    const methods = useForm<any>({
        mode: "onChange",
        reValidateMode: "onChange",
    });
    const {
        handleSubmit,
        register,
        reset,
        setError,
        setValue,
        watch,
        formState: { errors, touchedFields },
    } = methods;

    const vatNumber = watch("vatNumber");
    const country = watch("country");

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValue(name, value) ;
    } ;

    const validateOrganizationIdentifier = (id: string): string|boolean => {
        if (!$ok(id)) { return false; }
        
        if ($length(id) < 2) { return "Le format du numéro de TVA est invalide" ; }

        const countryCode = id.slice(0, 2).toUpperCase();
        if (!$ok(countries?.find(country => country.code === countryCode))) {
            return "Le numéro de TVA ne correspond à aucun des pays acceptés";
        }

        if (!$isstring(vatRegistrationNumber(id, country))) {
            return "Le format du numéro de TVA est invalide";
        }

        return true ;
    }

    const onSubmit: SubmitHandler<any> = (data: UserInfosValues, event) => {
        const dataUser: UserParameters = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: $email(data.email)!,
            apid: $UUID(data.apid)!,
        } ;
        if ($ok(data.organizationName)) {
            dataUser.organizationName = data.organizationName ;
        }
        if ($ok(data.vatNumber)) {
            dataUser.vatNumber = data.vatNumber ;
        }
        if ($ok(data.city) && (data.city.length > 0)) {
            dataUser.organizationAddress = {
                streetNumber: data.streetNumber,
                street: data.street,
                complement: data.streetComplement,
                zipCode: data.zipCode,
                city: data.city,
                country: $country(data.country)!
            }
        }

        event?.preventDefault() ;
        const button = (event?.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
        const buttonIndex = button.getAttribute('data-index');
        if ($ok(buttonIndex)) {
            actionButtons[buttonIndex!].onSubmit(dataUser) ;
        }
    } ;

    useEffect(() => {
        if ($ok(vatNumber) && touchedFields.vatNumber)
        {
            setValue("vatNumber", vatNumber, { shouldValidate: true });
        }
    }, [country]);

    useEffect(() => {
        if ($ok(vatNumber) && $length(vatNumber) >= 2) {
            const countryCode = vatNumber.slice(0, 2).toUpperCase();
            if ($ok(countries?.find(country => country.code === countryCode))) {
                setValue("country", countryCode);
            }
        }
    }, [vatNumber]);

    useEffect(() => {
        reset();
        setDisabledUserInfos($ok(data));
        setValue("lastName", data?.lastName || "") ;
        setValue("firstName", data?.firstName || "") ;
        setValue("email", data?.email.toString() || "") ;
        setValue("apid", data?.apid || "") ;

        setValue("organizationName", data?.organizationName || "") ;
        setDisabledOrganizationName(!isCreation && $ok(data?.organizationName));
        if (!isCreation && !$ok(data?.organizationName)) { 
            setError('organizationName', { type: 'manual', message: 'Champ obligatoire' });
        }
        setValue("vatNumber", data?.vatNumber || "");
        setDisabledVATNumber(!isCreation && $ok(data?.vatNumber));
        if (!isCreation && !$ok(data?.vatNumber)) { 
            setError('vatNumber', { type: 'manual', message: 'Le numéro de TVA intracommunautaire est requis' });
        }
        setValue("streetNumber", data?.organizationAddress?.streetNumber || "") ;
        setValue("street", data?.organizationAddress?.street || "") ;
        setValue("streetComplement", data?.organizationAddress?.complement) ;
        setValue("zipCode", data?.organizationAddress?.zipCode) ;
        setValue("city", data?.organizationAddress?.city || "") ;
        setDisabledAddress(!isCreation && $ok(data?.organizationAddress?.city));
        if (!isCreation && !$ok(data?.organizationAddress?.city)) { 
            setError('city', { type: 'manual', message: 'Champ obligatoire' });
        }
        setValue("country", data?.organizationAddress?.country || Countries.FR) ;
        setForceUpdate(forceUpdate + 1) ;
    }, [data, setError]) ;

    return (
      <>
        <ColumnTitle display={isMOBILE ? "block" : "none"} color={$g1Color('global.write')}>
            <Flex alignItems="center" as="button" onClick={onBack} width="100%">
                <Center color={$g1Color('global.icons.navigation.previous.write')}><G1Icon.Back /></Center>
                <Text>Retour</Text>
            </Flex>
        </ColumnTitle>
        <Box visibility={visibility} width="100%" mt={10} maxW={maxWidth}>
            <SeaShellBox component="form">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                    <FormControl isRequired isDisabled={disabledUserInfos} isInvalid={$ok(errors.lastName)}>
                        <FormLabel fontSize="1.25em">Nom</FormLabel>
                        <G1Input component="form" registerInput={{ ...register("lastName", { required: true, pattern: /^[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*$/i }) }} textTransform="uppercase" onChange={handleChange} />
                        {errors.lastName && <ErrorField fieldName="nom" error="Le champ Nom est requis ou est invalide" />}
                    </FormControl>

                    <FormControl isRequired isDisabled={disabledUserInfos} isInvalid={$ok(errors.firstName)} marginTop={3}>
                        <FormLabel fontSize="1.25em">Prénom</FormLabel>
                        <G1Input component="form" registerInput={{ ...register("firstName", { required: true, pattern: /^[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*$/i }) }} textTransform="capitalize" onChange={handleChange} />
                        {errors.firstName && <ErrorField fieldName="prénom" error="Le champ Prénom est requis ou est invalide" />}
                    </FormControl>

                    <FormControl isRequired isDisabled={disabledUserInfos} isInvalid={$ok(errors.email)} marginTop={3}>
                        <FormLabel fontSize="1.25em">Adresse email</FormLabel>
                        <G1Input component="form" type="email" registerInput={{ ...register("email", { required: true, pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i }) }} textTransform="lowercase" autoComplete="true" onChange={handleChange} />
                        {errors.email && <ErrorField fieldName="email" error="Le champ email est requis ou est invalide" />}
                    </FormControl>

                    <Box mt={3} width="100%">
                        <CorporationForm key={forceUpdate} label="Nom de l'entreprise" required={true} disabledOrganizationName={disabledOrganizationName} disabledVATNumber={disabledVATNumber} disabledAddress={disabledAddress}
                                         countries={countries} errors={errors} register={register} onChange={handleChange} validate={validateOrganizationIdentifier} />
                    </Box>

                    { $ok(data) ? 
                        <>
                            <Text marginTop="20px" fontSize="md" fontWeight="bold" display="inline-block">
                                Profil:
                            </Text>
                            <Text marginTop="20px" marginLeft="5px" fontSize="md" display="inline-block">
                                { userRoleAndStatus(data?.userFlags!, false) }
                            </Text>
                     </>
                        : ""
                    }

                    <FormControl>
                        <G1Input component="form" name="apid" type="hidden" />
                    </FormControl>

                    <Flex direction="row" justifyContent="center" marginTop="10px">
                        { actionButtons.length === 0 ? '' :
                                actionButtons.map((button, index) => (
                                    <G1Button type="submit" component="form" variant={button.variant} title={button.title} marginRight="2" fontSize="md" disabled={button.disabled} key={index} data-index={index} />
                                ))
                        }
                    </Flex>
                </form>
            </SeaShellBox>
        </Box>
      </>
    ) ;
} ;